import React from 'react';
import {
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { styled } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import moment from 'moment';
import BlockIcon from '@mui/icons-material/Block';
import { Link } from 'react-router-dom';
import { UserKindEnum } from '../../../../types/enums';
import { User } from '../types/types';

const USER_COLUMN_WIDTH = 334;

const StyledTableCell = styled(TableCell)({
  border: 0,
  borderRight: '1px solid #E9EEF1',
  '&:last-child': {
    borderRight: 'none',
  },
  verticalAlign: 'middle',
  '&.header-cell': {
    fontWeight: 600,
  },
  '&.user-type-cell, &.registration-cell, &.last-seen-cell': {
    minWidth: '150px',
  },
  '&.frozen-cell': {
    position: 'sticky',
    zIndex: 1,
    boxSizing: 'border-box',
    backgroundColor: '#fff',
  },
  '&.header-cell.frozen-cell': {
    zIndex: 2,
  },
  '&.user-column': {
    left: 0,
    width: `${USER_COLUMN_WIDTH}px`,
    minWidth: `${USER_COLUMN_WIDTH}px`,
    maxWidth: `${USER_COLUMN_WIDTH}px`,
    boxShadow: '2px 0 4px -3px rgba(42, 56, 66, 0.35)',
  },
  '&.foreign-frozen-cell': {
    backgroundColor: '#2A3842',
  },
});

const StyledIconButton = styled(IconButton)`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: 2px solid #c5dfff;
  border-radius: 12px;

  &.foreign-item {
    color: #fff;
  }
`;

const UserCell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  width: 100%;
  min-width: 0;
`;

const UserInfo = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 230px;
`;

const UserNameRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;

  &.foreign-item {
    color: #fff;
  }
`;

const UserNameLink = styled(Link)`
  font-weight: bold;
  font-size: 16px;
  color: #1650ff;
  text-decoration: none;
  cursor: pointer;
  min-width: 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #1650ff;
    outline-offset: 2px;
    border-radius: 2px;
  }

  &.foreign-item {
    color: #fff;

    &:hover {
      color: #c5dfff;
    }

    &:focus-visible {
      color: #c5dfff;
      outline-color: #c5dfff;
    }
  }
`;

const UserRole = styled('div')`
  color: gray;
  font-size: 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const UserAgency = styled('span')`
  color: #01439b;
  font-size: 14px;
`;

const UserDate = styled('span')`
  font-size: 14px;
  &.foreign-item {
    color: #fff;
  }
`;

const StyledTableContainer = styled(TableContainer)({
  background: 'transparent',
  boxShadow: 'none',
  overflowX: 'auto',
  maxWidth: '1003px',
  '&::-webkit-scrollbar': {
    borderRadius: '9px',
    height: '6px',
    backgroundColor: 'rgba(233, 238, 241, 1)',
    width: '300px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(42, 56, 66, 1)',
    borderRadius: '9px',
    height: '6px',
  },
});

const StyledTableRow = styled(TableRow)(`
border: 1px solid #E9EEF1;
background: inherit;

&.foreign-row {
  background-color: #2A3842;
}
`);

const StyledTableHead = styled(TableHead)({
  background: '#fff',
});

const formatDate = (value: string | null) =>
  value !== null ? moment(value).format('D MMMM YYYY, h:mm a') : null;

interface UserTableProps {
  users: User[];
  total: number;
  handleMenuClick: (event: React.MouseEvent<Element>, userId: number) => void;
}

export const UserTable: React.FC<UserTableProps> = ({ users, total, handleMenuClick }) => {
  return (
    <StyledTableContainer>
      <Table>
        <StyledTableHead>
          <StyledTableRow>
            <StyledTableCell className='header-cell frozen-cell user-column'>
              All users ({total})
            </StyledTableCell>
            <StyledTableCell className='header-cell'>Ambassador</StyledTableCell>
            <StyledTableCell className={`header-cell`}>Id</StyledTableCell>
            <StyledTableCell className={`header-cell user-type-cell`}>User type</StyledTableCell>
            <StyledTableCell className={`header-cell`}>Phone number</StyledTableCell>
            <StyledTableCell className={`header-cell`}>Email</StyledTableCell>
            <StyledTableCell className={`header-cell`}>Active listings</StyledTableCell>
            <StyledTableCell className={`header-cell`}>Draft listings</StyledTableCell>
            <StyledTableCell className={`header-cell`}>Archived listings</StyledTableCell>
            <StyledTableCell className={`header-cell`}>Notifications</StyledTableCell>
            <StyledTableCell className={`header-cell`}>Client requests</StyledTableCell>
            <StyledTableCell className={`header-cell registration-cell`}>
              Registration date
            </StyledTableCell>
            <StyledTableCell className={`header-cell last-seen-cell`}>Last seen</StyledTableCell>
          </StyledTableRow>
        </StyledTableHead>
        <TableBody>
          {users.map((user, index) => (
            <StyledTableRow key={index} className={`${user.kind}-row`}>
              <StyledTableCell
                className={`frozen-cell user-column${
                  user.kind === 'foreign' ? ' foreign-frozen-cell' : ''
                }`}
              >
                <UserCell>
                  <UserInfo>
                    <UserNameRow className={`${user.kind}-item`}>
                      <UserNameLink className={`${user.kind}-item`} to={`/users/${user.id}`}>
                        {user.name}
                      </UserNameLink>
                      {user.status === 'blocked' && <BlockIcon fontSize='small' />}
                    </UserNameRow>
                    <UserRole>
                      {user.employer.name && <UserAgency>{user.employer.name}</UserAgency>}
                    </UserRole>
                  </UserInfo>
                  {user.kind !== UserKindEnum.Admin && (
                    <StyledIconButton
                      aria-label='more'
                      aria-controls='user-menu'
                      aria-haspopup='true'
                      onClick={(event) => handleMenuClick(event, user.id)}
                      className={`${user.kind}-item`}
                    >
                      <MoreVertIcon />
                    </StyledIconButton>
                  )}
                </UserCell>
              </StyledTableCell>
              <StyledTableCell>
                {user.isAmbassador && <Chip color='primary' label='Yes' size='small' />}
              </StyledTableCell>
              <StyledTableCell>
                <UserDate className={`${user.kind}-item`}>{user.id}</UserDate>
              </StyledTableCell>
              <StyledTableCell>
                <UserDate className={`${user.kind}-item`}>{user.kind}</UserDate>
              </StyledTableCell>
              <StyledTableCell>
                <UserDate className={`${user.kind}-item`}>{user.phone}</UserDate>
              </StyledTableCell>
              <StyledTableCell>
                <UserDate className={`${user.kind}-item`}>{user.email}</UserDate>
              </StyledTableCell>
              <StyledTableCell>
                <UserDate className={`${user.kind}-item`}>{user.activeListings}</UserDate>
              </StyledTableCell>
              <StyledTableCell>
                <UserDate className={`${user.kind}-item`}>{user.draftListings}</UserDate>
              </StyledTableCell>
              <StyledTableCell>
                <UserDate className={`${user.kind}-item`}>{user.archivedListings}</UserDate>
              </StyledTableCell>
              <StyledTableCell>
                <UserDate className={`${user.kind}-item`}>{user.notifications}</UserDate>
              </StyledTableCell>
              <StyledTableCell>
                <UserDate className={`${user.kind}-item`}>{user.clientRequests}</UserDate>
              </StyledTableCell>
              <StyledTableCell>
                <UserDate className={`${user.kind}-item`}>{formatDate(user.createdAt)}</UserDate>
              </StyledTableCell>
              <StyledTableCell>
                <UserDate className={`${user.kind}-item`}>{formatDate(user.lastSeen)}</UserDate>
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};
